/** DSD 미리보기 패널 컴포넌트 */
import { forwardRef } from "react";
import {
  DSDChunkInfo,
  DSDSubChunkInfo,
  DsdHeaderTag,
  MatchedIxdCell,
} from "@/apis/compare/route";
import {
  DsdPreviewRenderer,
  type DsdDlRegionView,
  type DsdLineItemRegionView,
  type DsdMemberRegionView,
  type DsdPeriodRegionView,
  type DsdPositionReview,
  type DsdPreviewRendererHandle,
} from "../dsd-preview-renderer";

export type PreviewPanelHandle = DsdPreviewRendererHandle;

interface PreviewPanelProps {
  activeChunk?:
    | DSDChunkInfo
    | {
        id: string;
        title: string;
        subChunks: DSDSubChunkInfo[];
      }
    | null;
  onValueClick?: (
    value: string,
    matchedIxdCells: MatchedIxdCell[],
    mostProperCellId?: string | null,
    dlViewId?: string | null,
    period?: string | null,
  ) => void;
  onShouldTagCellClick?: (
    value: string,
    inferredCellId: string | null,
    dlViewId?: string | null,
    period?: string | null,
  ) => void;
  onNoMatchCellClick?: () => void;
  focusedDsdResetKey?: number;
  /** IXD 선택에 매칭되는 DSD 셀 dsdId 집합 (null/undefined 면 강조 없음 — 나머지는 dim) */
  counterpartDsdIds?: Set<string> | null;
  /** 헤더 셀 dsdId → 매핑된 태그 (우클릭 태그 popover 용) */
  headerTagsByDsdId?: Map<string, DsdHeaderTag> | null;
  /** element/member id → 한글 라벨 (inferredCellId 파싱 라벨링용) */
  ixdLabelMap?: Map<string, string> | null;
  /** 우클릭 데이터 셀의 inferredCellId 로 IXD 포커싱 위임 */
  onInferredFocus?: (
    inferredCellId: string,
    value: string,
    dlViewId?: string | null,
    period?: string | null,
  ) => void;
  /** 우클릭 헤더 셀 → 대응 IXD 헤더로 포커싱 위임 */
  onHeaderFocus?: (tag: DsdHeaderTag) => void;
  /** 드래그로 선택된 DSD 셀 dsdId 목록 변경 콜백(매칭 IXD 셀 강조용) */
  onSelectedDsdCellIdsChange?: (dsdIds: string[]) => void;
  /** 이 chunk 의 DL 영역들 (선택 셀이 속한 영역 강조용) */
  dlRegions?: DsdDlRegionView[] | null;
  periodRegions?: DsdPeriodRegionView[] | null;
  lineItemRegions?: DsdLineItemRegionView[] | null;
  memberRegions?: DsdMemberRegionView[] | null;
  reviewAxisId?: string | null;
  /** 값 위치 검토가 켜져 있을 때 DSD 표에 그릴 것 */
  positionReview?: DsdPositionReview | null;
}

export const PreviewPanel = forwardRef<PreviewPanelHandle, PreviewPanelProps>(
  function PreviewPanel(
    {
      activeChunk = null,
      onValueClick,
      onShouldTagCellClick,
      onNoMatchCellClick,
      focusedDsdResetKey,
      counterpartDsdIds,
      headerTagsByDsdId,
      ixdLabelMap,
      onInferredFocus,
      onHeaderFocus,
      onSelectedDsdCellIdsChange,
      dlRegions,
      periodRegions,
      lineItemRegions,
      memberRegions,
      reviewAxisId,
      positionReview,
    },
    ref,
  ) {
    return (
      <div className="flex flex-col flex-1 min-w-0 min-h-0 border border-neutral-200 rounded-xl overflow-hidden bg-white shadow-sm">
        <div className="flex-1 min-h-0 overflow-hidden">
          {activeChunk ? (
            <DsdPreviewRenderer
              ref={ref}
              chunkId={activeChunk.id}
              subChunks={activeChunk.subChunks}
              focusedDsdResetKey={focusedDsdResetKey}
              onValueClick={onValueClick}
              onShouldTagCellClick={onShouldTagCellClick}
              onNoMatchCellClick={onNoMatchCellClick}
              counterpartDsdIds={counterpartDsdIds}
              headerTagsByDsdId={headerTagsByDsdId}
              ixdLabelMap={ixdLabelMap}
              onInferredFocus={onInferredFocus}
              onHeaderFocus={onHeaderFocus}
              onSelectedCellIdsChange={onSelectedDsdCellIdsChange}
              dlRegions={dlRegions}
              periodRegions={periodRegions}
              lineItemRegions={lineItemRegions}
              memberRegions={memberRegions}
              reviewAxisId={reviewAxisId}
              positionReview={positionReview}
            />
          ) : null}
        </div>
      </div>
    );
  },
);
